import React, { useState, useRef, useEffect } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
// React Icons
import { MdOutlineAddBox, MdAssignmentReturn } from 'react-icons/md'
import { TbCurrencyDollar, TbFileInvoice } from 'react-icons/tb'
import { FiSettings } from 'react-icons/fi'
import { AiOutlineDashboard } from 'react-icons/ai'
import { LuLayoutDashboard } from 'react-icons/lu'
import { CiDiscount1 } from 'react-icons/ci'
import { HiOutlineDocumentReport } from 'react-icons/hi'
import { PiUsersThreeThin } from 'react-icons/pi'
import { LiaUsersCogSolid, LiaUsersSolid } from 'react-icons/lia'
import { RiQuoteText } from 'react-icons/ri'
import { FaFileArchive, FaFileInvoice } from 'react-icons/fa'
import { FaChevronUp, FaChevronDown } from 'react-icons/fa' // Added arrow icons

// Custom hooks
import { useTenant } from '../../hooks'
import { fetchUserPermissions, getAppPermission, getCurrentAppId } from '../../utils/permissionsFilter'

const MenuSide = ({openMenu, setOpenMenu}) => {
    const [t] = useTranslation('global')
    const {tenantUsername} = useTenant();
    const authUser = useSelector((state) => state.auth.user);
    const location = useLocation();
    const scrollContainerRef = useRef(null);
    const [hoverTooltip, setHoverTooltip] = useState(null);
    const [isTooltipVisible, setIsTooltipVisible] = useState(false);
    const tooltipHideTimeoutRef = useRef(null);
    const [userRole, setUserRole] = useState('user');
    const [roleLoaded, setRoleLoaded] = useState(false);
    
    useEffect(() => {
        let active = true;
        const loadUserRole = async () => {
            if (!authUser?._id) {
                setRoleLoaded(false);
                return;
            }
            try {
                const appId = await getCurrentAppId();
                if (!appId) {
                    if (active) {
                        setUserRole('user');
                        setRoleLoaded(true);
                    }
                    return;
                }
                const permissions = await fetchUserPermissions(authUser._id, true);
                const appPermission = getAppPermission(appId, permissions);
                if (active) {
                    setUserRole(appPermission?.userRole || 'user');
                    setRoleLoaded(true);
                }
            } catch (error) {
                if (active) {
                    setUserRole('user');
                    setRoleLoaded(true);
                }
            }
        };
        loadUserRole();
        return () => {
            active = false;
        };
    }, [authUser?._id]);
    
    // Reset tooltip state when menu expands
    useEffect(() => {
        if (openMenu) {
            setIsTooltipVisible(false);
            setHoverTooltip(null);
        }
    }, [openMenu]);

    // Reset tooltip state on every route change. The clicked icon's <li> stays
    // hovered (the cursor doesn't move on click), so nothing re-fires
    // onItemEnter/onItemLeave across navigation - the tooltip badge just keeps
    // showing the position/label captured from the PREVIOUS page. If the new
    // page changes the sidebar's own layout (e.g. a permission-gated item
    // appears/disappears, shifting icons up or down), that stale `top` becomes
    // wrong the instant the route changes, so the green badge visibly jumps or
    // flashes at the wrong spot - the flicker reported on every navigation.
    useEffect(() => {
        if (tooltipHideTimeoutRef.current) {
            clearTimeout(tooltipHideTimeoutRef.current);
            tooltipHideTimeoutRef.current = null;
        }
        setIsTooltipVisible(false);
        setHoverTooltip(null);
    }, [location.pathname]);
    
    // Function to handle scrolling with arrow buttons
    const scrollMenu = (direction) => {
        const el = scrollContainerRef.current;
        if (!el) return;

        const scrollAmount = direction === 'up' ? -120 : 120;
        el.scrollBy({ top: scrollAmount, behavior: 'smooth' });

        setIsTooltipVisible(false);
        setHoverTooltip(null);
    };

    const canSeeAdminItems = roleLoaded && userRole !== 'user';

    const setTooltipFromElement = (title, element) => {
        if (!title || openMenu) return;
        if (tooltipHideTimeoutRef.current) {
            clearTimeout(tooltipHideTimeoutRef.current);
            tooltipHideTimeoutRef.current = null;
        }
        const rect = element.getBoundingClientRect();
        const dir = document?.documentElement?.dir || 'rtl';
        const isRTL = dir === 'rtl';
        setIsTooltipVisible(false);
        setHoverTooltip({
            title,
            top: rect.top,
            height: rect.height,
            isRTL,
            left: rect.right,
            right: window.innerWidth - rect.left,
        });
        requestAnimationFrame(() => setIsTooltipVisible(true));
    };

    const onItemEnter = (title) => (e) => setTooltipFromElement(title, e.currentTarget);
    const onItemLeave = () => {
        setIsTooltipVisible(false);
        tooltipHideTimeoutRef.current = setTimeout(() => {
            setHoverTooltip(null);
            tooltipHideTimeoutRef.current = null;
        }, 180);
    };

    // Native scrolling handles wheel naturally; no custom wheel handler needed.

    return (
        <div 
        className={`z-50 flex h-screen flex-col justify-between items-center overflow-visible transition-all duration-500 ease-out primary-bg-color ${openMenu ? 'fixed w-10 flex md:w-40 md:relative' : 'fixed w-0 hidden md:flex md:w-[52px]'}`}> 
        
        {/* Up Arrow - Always visible */}
        <div 
            className="cursor-pointer w-full flex justify-center p-2 active-hover text-white sticky top-0 primary-bg-color z-20" 
            onClick={() => scrollMenu('up')}
        >
            <FaChevronUp size={16} />
        </div>
        
        {/* Menu Container with reduced fixed height */}
        <div 
            ref={scrollContainerRef}
            className="no-scrollbar overflow-y-auto overflow-x-hidden flex-1 min-h-0 w-full flex flex-col items-center relative"
        >
                <ul 
                    className="cursor-default list-none flex flex-col gap-4 items-center text-white text-xs w-full"
                    style={{ paddingTop: '8px', paddingBottom: 'calc(var(--footer-height, 28px) + 72px)' }}
                >
                    <li
                        data-title={t('menu.Dashboard')}
                        className={`w-full menu-border-color active-hover ${!openMenu ? 'tooltip relative flex justify-center' : ''}`}
                        onMouseEnter={onItemEnter(t('menu.Dashboard'))}
                        onMouseLeave={onItemLeave}
                    >
                        <NavLink to={`/${tenantUsername}`} className={`flex items-center gap-2 w-full ${openMenu ? 'justify-start' : 'justify-center'} py-3`} end>
                            <AiOutlineDashboard size={26} className="z-50" />
                            {!openMenu && <span className="w-0 h-full bg-white absolute end-0 top-0" />}
                            {openMenu && <span className='whitespace-nowrap hidden md:flex'>{t('menu.Dashboard')}</span>}
                        </NavLink>
                    </li>
                    <li
                        data-title={t('menu.Invoices')}
                        className={`w-full gap-2 flex items-center menu-border-color active-hover ${!openMenu ? 'tooltip relative justify-center' : ''}`}
                        onMouseEnter={onItemEnter(t('menu.Invoices'))}
                        onMouseLeave={onItemLeave}
                    >
                        <NavLink to={`/${tenantUsername}/invoices`} className={`flex items-center gap-2 w-full ${openMenu ? 'justify-start' : 'justify-center'} py-3`} end>
                            <TbFileInvoice size={26} className="z-50" />
                            {!openMenu && <span className="w-0 h-full bg-white absolute end-0 top-0" />}
                            {openMenu && <span className='whitespace-nowrap hidden md:flex'>{t('menu.Invoices')}</span>}
                        </NavLink>
                    </li>
                    <li
                        data-title={t('menu.CreditNotes')}
                        className={`w-full gap-2 flex items-center menu-border-color active-hover ${!openMenu ? 'tooltip relative justify-center' : ''}`}
                        onMouseEnter={onItemEnter(t('menu.CreditNotes'))}
                        onMouseLeave={onItemLeave}
                    >
                        <NavLink to={`/${tenantUsername}/credit-notes`} className={`flex items-center gap-2 w-full ${openMenu ? 'justify-start' : 'justify-center'} py-3`}>
                            <MdAssignmentReturn size={26} className="z-50" />
                            {!openMenu && <span className="w-0 h-full bg-white absolute end-0 top-0" />}
                            {openMenu && <span className='whitespace-nowrap hidden md:flex'>{t('menu.CreditNotes')}</span>}
                        </NavLink>
                    </li>
                    {/* <li className={`w-full gap-2 flex items-center menu-border-color pb-1 active-hover ${!openMenu && 'justify-center'}`}>
                        <NavLink to={`/${tenantUsername}/invoice-requisition`} className={`flex items-center gap-2`} end>
                            <FaFileArchive size={18} title={t('menu.sales_order')} />
                            {openMenu && <span className='whitespace-nowrap hidden md:flex'>{t('menu.sales_order')}</span>}
                        </NavLink>
                    </li> */}
                    <li
                        data-title={t('menu.Quotes')}
                        className={`w-full menu-border-color active-hover ${!openMenu ? 'tooltip relative flex justify-center' : ''}`}
                        onMouseEnter={onItemEnter(t('menu.Quotes'))}
                        onMouseLeave={onItemLeave}
                    >
                        <NavLink to={`/${tenantUsername}/quotes`} className={`flex items-center gap-2 w-full ${openMenu ? 'justify-start' : 'justify-center'} py-3`} end>
                            <RiQuoteText size={26} className="z-50" />
                            {!openMenu && <span className="w-0 h-full bg-white absolute end-0 top-0" />}
                            {openMenu && <span className='whitespace-nowrap hidden md:flex'>{t('menu.Quotes')}</span>}
                        </NavLink>
                    </li>
                    <li
                        data-title={t('menu.Products')}
                        className={`w-full gap-2 flex items-center menu-border-color active-hover ${!openMenu ? 'tooltip relative justify-center' : ''}`}
                        onMouseEnter={onItemEnter(t('menu.Products'))}
                        onMouseLeave={onItemLeave}
                    >
                        <NavLink to={`/${tenantUsername}/products-home`} className={`flex items-center gap-2 w-full ${openMenu ? 'justify-start' : 'justify-center'} py-3`} end>
                            <LuLayoutDashboard size={26} className="z-50" />
                            {!openMenu && <span className="w-0 h-full bg-white absolute end-0 top-0" />}
                            {openMenu && <span className='whitespace-nowrap hidden md:flex'>{t('menu.Products')}</span>}
                        </NavLink>
                    </li>
                    {canSeeAdminItems && (
                        <li
                            data-title={t('invoice.payments')}
                            className={`w-full gap-2 flex items-center menu-border-color active-hover ${!openMenu ? 'tooltip relative justify-center' : ''}`}
                            onMouseEnter={onItemEnter(t('invoice.payments'))}
                            onMouseLeave={onItemLeave}
                        >
                            <NavLink to={`/${tenantUsername}/invoices/unpaid`} className={`flex items-center gap-2 w-full ${openMenu ? 'justify-start' : 'justify-center'} py-3`} end>
                                <TbCurrencyDollar size={26} className="z-50" />
                                {!openMenu && <span className="w-0 h-full bg-white absolute end-0 top-0" />}
                                {openMenu && <span className='whitespace-nowrap hidden md:flex'>{t('invoice.payments')}</span>}
                            </NavLink>
                        </li>
                    )}
                    <li
                        data-title={t('menu.Customers')}
                        className={`w-full gap-2 flex items-center menu-border-color active-hover ${!openMenu ? 'tooltip relative justify-center' : ''}`}
                        onMouseEnter={onItemEnter(t('menu.Customers'))}
                        onMouseLeave={onItemLeave}
                    >
                        <NavLink to={`/${tenantUsername}/customers`} className={`flex items-center gap-2 w-full ${openMenu ? 'justify-start' : 'justify-center'} py-3`} end>
                            <LiaUsersCogSolid size={26} className="z-50" />
                            {!openMenu && <span className="w-0 h-full bg-white absolute end-0 top-0" />}
                            {openMenu && <span className='whitespace-nowrap hidden md:flex'>{t('menu.Customers')}</span>}
                        </NavLink>
                    </li>
                    {canSeeAdminItems && (
                        <li
                            data-title={t('menu.Salesmans')}
                            className={`w-full gap-2 flex items-center menu-border-color active-hover ${!openMenu ? 'tooltip relative justify-center' : ''}`}
                            onMouseEnter={onItemEnter(t('menu.Salesmans'))}
                            onMouseLeave={onItemLeave}
                        >
                            <NavLink to={`/${tenantUsername}/salesmans`} className={`flex items-center gap-2 w-full ${openMenu ? 'justify-start' : 'justify-center'} py-3`} end>
                                <LiaUsersSolid size={26} className="z-50" />
                                {!openMenu && <span className="w-0 h-full bg-white absolute end-0 top-0" />}
                                {openMenu && <span className='whitespace-nowrap hidden md:flex'>{t('menu.Salesmans')}</span>}
                            </NavLink>
                        </li>
                    )}
                    {canSeeAdminItems && (
                        <li
                            data-title={t('menu.Reports')}
                            className={`w-full gap-2 flex items-center menu-border-color active-hover ${!openMenu ? 'tooltip relative justify-center' : ''}`}
                            onMouseEnter={onItemEnter(t('menu.Reports'))}
                            onMouseLeave={onItemLeave}
                        >
                            <NavLink to={`/${tenantUsername}/reports`} className={`flex items-center gap-2 w-full ${openMenu ? 'justify-start' : 'justify-center'} py-3`} end>
                                <HiOutlineDocumentReport size={26} className="z-50" />
                                {!openMenu && <span className="w-0 h-full bg-white absolute end-0 top-0" />}
                                {openMenu && <span className='whitespace-nowrap hidden md:flex'>{t('menu.Reports')}</span>}
                            </NavLink>
                        </li>
                    )}
                    {canSeeAdminItems && (
                        <li
                            data-title={t('menu.Settings')}
                            className={`w-full gap-2 flex items-center menu-border-color active-hover ${!openMenu ? 'tooltip relative justify-center' : ''}`}
                            onMouseEnter={onItemEnter(t('menu.Settings'))}
                            onMouseLeave={onItemLeave}
                        >
                            <NavLink to={`/${tenantUsername}/settings`} className={`flex items-center gap-2 w-full ${openMenu ? 'justify-start' : 'justify-center'} py-3`} end>
                                <FiSettings size={26} className="z-50" />
                                {!openMenu && <span className="w-0 h-full bg-white absolute end-0 top-0" />}
                                {openMenu && <span className='whitespace-nowrap hidden md:flex'>{t('menu.Settings')}</span>}
                            </NavLink> 
                        </li>
                    )}
                    {/* <li className={`w-full menu-border-color pb-1 active-hover ${!openMenu && 'flex justify-center'}`}>
                        <NavLink to={`/${tenantUsername}/quotes`} className={`flex items-center gap-2`} end>
                            <RiQuoteText size={20} title={t('menu.Quotes')} />
                            {openMenu && <span className='whitespace-nowrap hidden md:flex'>{t('menu.Quotes')}</span>}
                        </NavLink>
                    </li>
                    <li className={`w-full gap-2 flex items-center menu-border-color pb-1 active-hover ${!openMenu && 'justify-center'}`}>
                        <NavLink to={`/${tenantUsername}/products-home`} className={`flex items-center gap-2`} end>
                            <LuLayoutDashboard size={20} title={t('menu.Products')} />
                            {openMenu && <span className='whitespace-nowrap hidden md:flex'>{t('menu.Products')}</span>}
                        </NavLink>
                    </li>
                    <li className={`w-full gap-2 flex items-center menu-border-color pb-1 active-hover ${!openMenu && 'justify-center'}`}>
                        <NavLink to={`/${tenantUsername}/invoices/unpaid`} className={`flex items-center gap-2`} end>
                            <TbCurrencyDollar size={20} title={t('invoice.payments')} />
                            {openMenu && <span className='whitespace-nowrap hidden md:flex'>{t('invoice.payments')}</span>}
                        </NavLink>
                    </li>
                    <li className={`w-full gap-2 flex items-center menu-border-color pb-1 active-hover ${!openMenu && 'justify-center'}`}>
                        <NavLink to={`/${tenantUsername}/customers`} className={`flex items-center gap-2`} end>
                            <LiaUsersCogSolid size={20} title={t('menu.Customers')} />
                            {openMenu && <span className='whitespace-nowrap hidden md:flex'>{t('menu.Customers')}</span>}
                        </NavLink>
                    </li>
                    <li className={`w-full gap-2 flex items-center menu-border-color pb-1 active-hover ${!openMenu && 'justify-center'}`}>
                        <NavLink to={`/${tenantUsername}/salesmans`} className={`flex items-center gap-2`} end>
                            <LiaUsersSolid size={20} title={t('menu.Salesmans')} />
                            {openMenu && <span className='whitespace-nowrap hidden md:flex'>{t('menu.Salesmans')}</span>}
                        </NavLink>
                    </li>
                    <li className={`w-full gap-2 flex items-center menu-border-color pb-1 active-hover ${!openMenu && 'justify-center'}`}>
                        <NavLink to={`/${tenantUsername}/reports`} className={`flex items-center gap-2`} end>
                            <HiOutlineDocumentReport size={20} title={t('menu.Reports')} />
                            {openMenu && <span className='whitespace-nowrap hidden md:flex'>{t('menu.Reports')}</span>}
                        </NavLink>
                    </li>
                    <li className={`w-full gap-2 flex items-center menu-border-color pb-1 active-hover ${!openMenu && 'justify-center'}`}>
                        <NavLink to={`/${tenantUsername}/settings`} className={`flex items-center gap-2`} end>
                            <FiSettings size={20} title={t('menu.Settings')} />
                            {openMenu && <span className='whitespace-nowrap hidden md:flex'>{t('menu.Settings')}</span>}
                        </NavLink>
                    </li> */}
                </ul>
            </div>

            {!openMenu && hoverTooltip?.title && (
                <div
                    className={`fixed z-[9999] flex items-center text-xs whitespace-nowrap transition-opacity duration-200 ease-out ${isTooltipVisible ? 'opacity-100' : 'opacity-0'}`}
                    style={{
                        top: hoverTooltip.top,
                        height: hoverTooltip.height,
                        background: 'var(--titleColor)',
                        color: 'var(--background-color)',
                        padding: '0 10px',
                        borderBottom: '1px solid white',
                        pointerEvents: 'none',
                        ...(hoverTooltip.isRTL
                            ? { right: hoverTooltip.right, borderRadius: '5px 0px 0px 5px' }
                            : { left: hoverTooltip.left, borderRadius: '0px 5px 5px 0px' }),
                    }}
                >
                    {hoverTooltip.title}
                </div>
            )}
            
            {/* Down Arrow - Always visible */}
            <div 
            // style={{background:"#1E1E2D"}}
            // style={{background:"#EEEEEE"}}
                className="cursor-pointer w-full flex justify-center p-2 active-hover text-white sticky bottom-0 primary-bg-color z-20" 
                onClick={() => scrollMenu('down')}
            >
                <FaChevronDown size={16} />
            </div>
        </div>
    )
}

export default MenuSide



